import {Component, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormControl} from '@angular/forms';
import {Subject} from 'rxjs';
import {finalize, take, takeUntil} from 'rxjs/operators';
import {Sort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {MAT_DIALOG_DATA, MatDialog} from '@angular/material/dialog';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {saveAs} from 'file-saver';
import {BasicPersonDetailDTO} from '../../../../ent/models/person/BasicPersonDetailDTO';
import {DateUtil} from '../../../util/DateUtil';
import {TableControl} from '../../../models/TableControl';
import {PagedFilterDTO} from '../../../models/PagedFilterDTO';
import {PagedResponseDTO} from '../../../models/PagedResponseDTO';
import {ClmRequestBuilder} from '../../../../clm/services/clm-request-builder';
import {SharedService} from '../../../services/shared-service';
import {PagedRequestDTO} from '../../../models/PagedRequestDTO';
import {PbmRequestBuilder} from '../../../../pbm/services/pbm-request-builder';
import {EmailService} from '../../../../pbm/services/email.service';
import {EmailDTO} from '../../../../pbm/models/email/EmailDTO';
import {CustomValidator} from '../../../validators';
import {detailsExpand} from '../../../animations';
import {FileUploadService} from '../../../services/file-upload.service';
import {CommunicationTriggerType} from '../../../types/communication-trigger-type.enum';

enum TableColumns {
  Icons = 'icons',
  EmailId = 'emailId',
  FromAddress = 'fromAddress',
  ToAddress = 'toAddress',
  CCAddress = 'ccAddress',
  CommunicationTriggerType = 'communicationTriggerType',
  TriggerId = 'triggerId',
  IsSent = 'isSent',
  PdfReport = 'pdf', // FE usage
}

enum IconName {
  Send = 'send',
  Reset = 'reset',
}
const PAGE_SIZE = 5;

@Component({
  selector: 'app-email-overview',
  templateUrl: './email-overview.component.html',
  styleUrls: [
    './email-overview.component.scss',
    '../../../../../../styles/global/_management-styles.scss',
    '../../../../../../styles/global/_dialog-styles.scss',
  ],
  animations: [detailsExpand]
})
export class EmailOverviewComponent implements OnInit, OnDestroy {
  readonly destroyed$: Subject<boolean> = new Subject();
  readonly dateFormat = DateUtil.dateFormat;
  readonly dateTimeFormat = DateUtil.dateTimeFormat;
  readonly controls = ClmRequestBuilder.getTableControls();
  readonly tableColumns = TableColumns;
  readonly CommunicationTriggerType = CommunicationTriggerType;
  readonly IconName = IconName;

  isEditMode: boolean;
  loggedInUserName: string;
  emailOverrideFormControl = new FormControl('', [CustomValidator.email]);
  emailTableControl: TableControl;
  emailExpandedRow: EmailDTO | null;
  emailDisplayedColumns: string[] = Object.values<TableColumns>(TableColumns);
  emailDataSource: MatTableDataSource<EmailDTO> = new MatTableDataSource<EmailDTO>([]);

  @ViewChild('emailPaginator', {static: false}) emailPaginator: MatPaginator;

  constructor(
    public dialog: MatDialog,
    private sharedService: SharedService,
    private emailService: EmailService,
    private fileUploadService: FileUploadService,
    @Inject(MAT_DIALOG_DATA) public basicPersonDetails: BasicPersonDetailDTO) {
    this.emailTableControl = new TableControl(
      this.controls.length,
      PAGE_SIZE,
      this.controls.pageNumber,
      this.controls.pageSizeOptions,
      TableColumns.EmailId,
      'desc',
      '',
      '');
  }


  get isDataExist(): boolean {
    return !!(this.emailDataSource && this.emailDataSource.data?.length);
  }

  ngOnInit(): void {
    this.loggedInUserName = this.sharedService.getLoggedInUserName();
    this.getListOfEmails(this.emailTableControl);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  initSubscriptions(): void {
    this.sharedService.getLoggedInProfile$()
      .pipe(
        take(1)
      )
      .subscribe({
        next: () => {
          this.loggedInUserName = this.sharedService.getLoggedInUserName();
        }
      });
  }

  sortData($event: Sort): void {
    if (!$event.direction) {
      return;
    }
    if (this.emailPaginator) {
      this.emailPaginator.firstPage();
      this.emailTableControl.pageNumber = 0;
    }

    if ($event.active === TableColumns.CommunicationTriggerType) {
      this.emailTableControl.sortField = 'communicationTriggerTypeByCommunicationTriggerTypeId.name';
    }
    else {
      this.emailTableControl.sortField = $event.active;
    }

    this.emailTableControl.sortDirection = $event.direction;
    this.getListOfEmails(this.emailTableControl);
  }

  pageChanged($event: PageEvent): void {
    this.emailTableControl.pageNumber = $event.pageIndex;
    this.emailTableControl.pageSize = $event.pageSize;
    this.getListOfEmails(this.emailTableControl);
  }

  onClickRow(event: MouseEvent, row: EmailDTO): void {
    const targetElement: HTMLElement = event.target as HTMLElement;
    if (targetElement.classList.contains('row-detail-container')) {
      event.preventDefault();
      event.stopPropagation();

      row.sendEmailChecked = !row.sendEmailChecked;
    }
    else {
      this.emailExpandedRow = this.emailExpandedRow === row ? null : row;
    }
  }

  onClickSendEmailConfirmed(event: MouseEvent, row: EmailDTO): void {
    event.preventDefault();
    event.stopPropagation();

    if (row.editedToAddress) {
      row.toAddress = row.editedToAddress;
    }

    // Front-end sent emails must override the toAddress always
    row.toAddressManualOverride = true;

    row.emailSentBusyCounter++;
    this.emailService.sendEmail(row)
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => {
          row.emailSentBusyCounter--;
          this.onClickToggleIcons(event, row);
        })
      )
      .subscribe({
        next: (_: EmailDTO) => {
          this.sharedService.openSnackBar('Email sent successfully!', 'Dismiss');
          this.getListOfEmails(this.emailTableControl);
        },
        error: (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openSnackBar(error?.businessMessage, 'Dismiss');
        }
      });
  }

  onClickResetEmailConfirmed(event: MouseEvent, row: EmailDTO): void {
    event.preventDefault();
    event.stopPropagation();

    row.emailSentBusyCounter++;
    this.emailService.resetEmail(row.emailId)
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => {
          row.emailSentBusyCounter--;
          this.onClickToggleResetEmailIcons(event, row);
        })
      )
      .subscribe({
        next: (_) => {
          this.sharedService.openSnackBar('Email reset successfully!', 'Dismiss');
          this.getListOfEmails(this.emailTableControl);
        },
        error: (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openSnackBar(error?.businessMessage || `Failed to reset email ${row.emailId}`, 'Dismiss');
        }
      });
  }

  onClickToggleIcons(event: MouseEvent, row: EmailDTO): void {
    event.preventDefault();
    event.stopPropagation();

    row.sendEmailChecked = !row.sendEmailChecked;
  }

  onClickToggleResetEmailIcons(event: MouseEvent, row: EmailDTO): void {
    event.preventDefault();
    event.stopPropagation();

    row.resetEmailConfirmation = !row.resetEmailConfirmation;
  }

  onClickGeneratePdfReport($event: MouseEvent, row: EmailDTO): void {
    $event.preventDefault();
    $event.stopPropagation();

    if (!row.triggerId || !row.attachment) {
      return;
    }

    const file = row.attachment.file;
    const name = row.attachment.name;

    const blobData = this.fileUploadService.base64toBlob(file, 'data:application/octet-stream;base64');
    const blob = new Blob([blobData]);
    saveAs(blob, name);
  }

  onClickEditAddress($event: MouseEvent, row: EmailDTO): void {
    $event.preventDefault();
    $event.stopPropagation();

    row.isEditEmail = true;
    const value: string = row?.editedToAddress ? row.editedToAddress : row.toAddress;
    this.emailOverrideFormControl.patchValue(value);
  }

  onClickCancelEditAddress($event: MouseEvent, row: EmailDTO): void {
    $event.preventDefault();
    $event.stopPropagation();

    row.isEditEmail = false;
    row.editedToAddress = '';
    this.emailOverrideFormControl.patchValue('');
    this.emailOverrideFormControl.updateValueAndValidity();
  }

  onClickSaveAddress($event: MouseEvent, row: EmailDTO): void {
    $event.preventDefault();
    $event.stopPropagation();

    if (!this.emailOverrideFormControl.value) {
      this.sharedService.openSnackBar('Provide valid email address', 'Dismiss');
      return;
    }
    else if (this.emailOverrideFormControl.invalid) {
      this.sharedService.openSnackBar('Invalid email address e.g Johndoe@email.com', 'Dismiss');
      return;
    }

    row.editedToAddress = this.emailOverrideFormControl.value;
    row.isEditEmail = false; // close edit mode
  }

  OnClickDuplicate(duplicate: boolean, row: EmailDTO): void {
    if (duplicate) {
      const newEmail: EmailDTO = this.getEmailDTOPayload(row);
      const updatedData: EmailDTO[] = this.emailDataSource.data.slice();
      updatedData.unshift(newEmail);
      this.emailDataSource.data = updatedData;
    }
  }

  getToAddress(row: EmailDTO): string {
    if (row.editedToAddress) {
      return row.editedToAddress;
    }
    return row.toAddress;
  }

  showIcon(row: EmailDTO, iconName: IconName): boolean {
    switch (iconName) {
    case IconName.Send:
      return (
        !row[TableColumns.IsSent] &&
        !row.sendEmailChecked &&
        !row.resetEmailConfirmation
      );
    case IconName.Reset:
      return (
        row[TableColumns.EmailId] &&
        !(row[TableColumns.CommunicationTriggerType]?.name === CommunicationTriggerType.ManualTriggerType) &&
        !row.sendEmailChecked &&
        !row.resetEmailConfirmation
      );
    default:
      return false;
    }
  }

  private getEmailDTOPayload(originalEmail: EmailDTO): EmailDTO {
    let payload: EmailDTO;
    payload = {
      ...originalEmail,
      emailId: null,
      isSent: 0 // This will enable send/edit icons
    } as EmailDTO;
    return payload;
  }

  private getListOfEmails(tableControl: TableControl): void {
    const pagedRequestDTO: PagedRequestDTO = PbmRequestBuilder.getListOfEmailPagedRequestDTO(tableControl);
    pagedRequestDTO.fieldMap = {};

    const personPagedFilterDTO: PagedFilterDTO = new PagedFilterDTO();
    personPagedFilterDTO.name = 'insuredPersonByInsuredPersonId.insuredPersonId';
    personPagedFilterDTO.value = String(this.basicPersonDetails.personId);
    pagedRequestDTO.fieldMap[`insuredPersonByInsuredPersonId`] = personPagedFilterDTO;

    this.emailService.getListOfEmails(pagedRequestDTO)
      .pipe(
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (response: PagedResponseDTO<EmailDTO>) => {
          response.resultList.forEach((item: EmailDTO): void => {
            item.sendEmailChecked = false;
            item.emailSentBusyCounter = 0;
          });
          this.emailDataSource.data = response.resultList;
          this.emailTableControl.length = response.totalRecords;
        },
        error: (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openSnackBar(error?.businessMessage, 'Dismiss');
        }
      });
  }
}
